import React, { useEffect, useMemo, useState } from 'react';
import { Table, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getOperationalIncome } from '../../../../redux/budgetSlice/budgetSlice';
import { useLocation } from 'react-router-dom';
import Loader from '../../LoadingSpinner/LoadingSpinner';
import { computeTotalRow } from '../../../../utils/VerticalTotalOfTable';
import { generateColumns, parseDMY } from '../../../../utils/utilFunctions';
import { addMonths, differenceInMonths, format, parse } from 'date-fns';
import { generateMonthExecution } from '../../../../pages/BudgetSubmission/constants';
import { getProjectTimeline } from '../../../../utils/getProjectTimeline';

const parseNum = (v) => {
        if (v === null || v === undefined) return 0;
        const n = Number(String(v).replace(/,/g, '').trim());
        return Number.isFinite(n) ? n : 0;
};

// Retained as a fallback for the very first render, before `systemDate` has
// loaded from Redux.
const getCurrentFYStart = () => {
        const today = new Date();
        return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

// NEW: FY start now comes from the admin-configured System Start Date
// (Redux `otherProjects.systemDate`, format DD-MM-YYYY) instead of today's date.
const parseSystemFYStart = (startDate) => {
        if (!startDate) return null;

        const [day, month, year] = startDate.split("-").map(Number);

        if (!day || !month || !year) return null;

        return month >= 4 ? year : year - 1; // Apr-Mar FY cycle
};

// OLD: COST_UPTO was rendered straight off the API's static field, and the
// "Upto" title/columns were anchored to this project's own UPTO_DATE.
// NEW: Upto = the API's baseline COST_UPTO plus every FY's "_formatted"
// value ("YYYY-YYYY_formatted") that falls strictly before the current
// (system-date-derived) FY - same "dynamic upto" rule used across the other
// reports/tables.
const getDynamicUpto = (row = {}, fyStart, baseField) => {
        let sum = parseNum(row[baseField]);

        Object.keys(row).forEach((key) => {
                const match = /^(\d{4})-\d{4}_formatted$/.exec(key);
                if (!match) return;

                const fyStartYear = Number(match[1]);
                if (fyStartYear < fyStart) {
                        sum += parseNum(row[key]);
                }
        });

        return sum.toFixed(2);
};

const OtherOperationalIncome = ({ version = "C" }) => {
        const { operationalIncomeData, operationalIncomeVersion } = useSelector((state) => state.budget);
        const { projectDates, loading } = useSelector((state) => state.budget);
        const { systemDate } = useSelector((state) => state.otherProjects);
        const { Text } = Typography;
        const location = useLocation();
        const ioNo = location.state?.ioNo || '--';
        const revisedNo = location.state?.rvNo || '--';
        const dispatch = useDispatch();

        // OLD: const CURRENT_FY_START = getCurrentFYStart(); (fixed once at import time)
        // NEW: derive it from the admin-configured System Start Date, falling back
        // to today's date until systemDate has loaded.
        const CURRENT_FY_START = useMemo(
                () => parseSystemFYStart(systemDate?.STARTDATE) ?? getCurrentFYStart(),
                [systemDate]
        );

        // OLD: title was anchored to this project's own UPTO_DATE.
        // const formattedUptoDate = projectDates?.UPTO_DATE
        //         ? format(parse(projectDates.UPTO_DATE, 'dd-MM-yyyy', new Date()), "MMM''yy")
        //         : '';
        const zeroDateParsed = parseDMY(projectDates?.ZERO_DATE);
        const uptoDateParsed = parseDMY(projectDates?.UPTO_DATE);
        const expectedDateParsed = parseDMY(projectDates?.EXPECTED_DATE);
        const unitPlanDateParsed = parseDMY(projectDates?.UNIT_PLAN_DATE);
        let startMonth = 4;
        let startYear = 2025;

        // OLD: rendered columns started the month after this project's own
        // UPTO_DATE.
        // NEW: anchored to the system-date-derived current FY (April of
        // CURRENT_FY_START) instead - consistent with the dynamic Upto value.
        // if (uptoDateParsed) {
        //         const startMonthDate = addMonths(uptoDateParsed, 1); //  Move to next month
        //         startMonth = startMonthDate.getMonth() + 1; // 1-based month
        //         startYear = startMonthDate.getFullYear();
        // }
        startMonth = 4;
        startYear = CURRENT_FY_START;
        const startMonthNumber =
                zeroDateParsed && uptoDateParsed
                        ? differenceInMonths(uptoDateParsed, zeroDateParsed) + 3
                        : 0;

        const { endYear, endMonth, totalMonth } = getProjectTimeline({
                zeroDate: zeroDateParsed,
                expectedDate: expectedDateParsed,
                unitPlanDate: unitPlanDateParsed,
        });
        // const totalMonth =
        //         zeroDateParsed && unitPlanDateParsed
        //                 ? differenceInMonths(unitPlanDateParsed, zeroDateParsed) + 3
        //                 : 0;
        // const endYear = expectedDateParsed ? expectedDateParsed.getFullYear() : 2026;
        // const endMonth = expectedDateParsed ? expectedDateParsed.getMonth() + 1 : 12;

        // let maxDataEndYear = 0;

        // const sourceData =
        //         version === "C"
        //                 ? operationalIncomeData
        //                 : operationalIncomeVersion;

        // if (sourceData?.length > 0) {
        //         const firstRow = sourceData[0];

        //         const fyKeys = Object.keys(firstRow).filter(
        //                 (key) => /^\d{4}-\d{4}$/.test(key)
        //         );

        //         if (fyKeys.length > 0) {
        //                 maxDataEndYear = Math.max(
        //                         ...fyKeys.map((fy) => Number(fy.split("-")[1]))
        //                 );
        //         }
        // }

        // const expectedEndYear = expectedDateParsed
        //         ? expectedDateParsed.getFullYear()
        //         : 0;

        // const finalEndYear = Math.max(maxDataEndYear, expectedEndYear);
        // const endYear = finalEndYear ? finalEndYear - 1 : startYear + 1;

        // const unitPlanEndYear = unitPlanDateParsed
        //         ? unitPlanDateParsed.getFullYear()
        //         : 0;

        // const finalEndYear = unitPlanEndYear;
        // const endYear = finalEndYear || startYear;

        // const endMonth = unitPlanDateParsed
        //         ? unitPlanDateParsed.getMonth() + 1
        //         : 12;



        const role = sessionStorage.getItem('role');

        const columns = [
                {
                        title: 'Description',
                        dataIndex: 'DESC',
                        key: 'DESC',
                        width: 250,
                        fixed: 'left',
                        align: 'center',
                        render: (text, record, index) => {
                                if (record.boldHeading) {
                                        return (
                                                <>
                                                        <Text strong>{text}</Text> &nbsp;
                                                </>
                                        );
                                }
                                return <div className="w-[200px]">{text}</div>;
                        },
                },
                {
                        // OLD: title: `Upto ${formattedUptoDate}`, (anchored to this project's own UPTO_DATE)
                        // NEW: anchored to the system-date-derived current FY.
                        title: `Upto Mar'${String(CURRENT_FY_START).slice(-2)}`,
                        dataIndex: 'COST_UPTO',
                        key: 'COST_UPTO',
                        width: 150,
                        align: 'center',
                        render: (text) => {
                                return <div className="text-right">{text}</div>;
                        },
                },
        ];

        const [data, setData] = useState([]);

        useEffect(() => {
                if (role !== 'ADMIN' && role !== 'SUPER_ADMIN') {
                        dispatch(getOperationalIncome({ IO_NO: ioNo, REV_NO: revisedNo }));
                }
        }, [dispatch]);

        useEffect(() => {
                const sourceData =
                        version === "C"
                                ? operationalIncomeData
                                : operationalIncomeVersion;

                if (sourceData) {
                        // OLD: computeTotalRow/sourceData.map read COST_UPTO straight off the
                        // API's static field.
                        // NEW: replace it with the dynamic value on every row first, so both
                        // the per-row column and the generic Total-row sum stay consistent.
                        const sourceDataWithDynamicUpto = sourceData.map((row) => ({
                                ...row,
                                COST_UPTO: getDynamicUpto(row, CURRENT_FY_START, "COST_UPTO"),
                        }));

                        const totalRow = computeTotalRow(sourceDataWithDynamicUpto, "Total");

                        delete totalRow.PACKAGE_NAME;
                        delete totalRow.PRODUCT_NAME;
                        delete totalRow.PRODUCT_TYPE;
                        delete totalRow.COMMERCIAL_NAME;
                        delete totalRow.PACKAGE_TYPE;

                        setData([
                                {
                                        key: "1",
                                        DESC: "Month of Execution",
                                        ...generateMonthExecution(
                                                startMonthNumber,
                                                startYear,
                                                startMonth,
                                                totalMonth
                                        ),
                                        boldHeading: true,
                                },
                                totalRow,
                                ...sourceDataWithDynamicUpto.map((item, index) => ({
                                        key: `operationalIncome-${index + 8}`,
                                        ...item,
                                })),
                        ]);
                }
        }, [operationalIncomeData, operationalIncomeVersion, version, CURRENT_FY_START]);


        return loading ? (
                <Loader />
        ) : (
                <Table
                        columns={generateColumns(
                                startYear,
                                endYear,
                                startMonth,
                                endMonth,
                                columns,
                                projectDates?.UNIT_PLAN_DATE
                        )}
                        dataSource={data}
                        pagination={false}
                        bordered
                        className="custom-ant-table"
                        scroll={{ x: 'max-content', y: 400 }}
                        rowClassName={(record, index) => {
                                if (index === 0) {
                                        return 'table-row-dark';
                                } else if (index === 1) {
                                        return 'custom_cost table-row-light-blue';
                                } else {
                                        return 'table-row-light';
                                }
                        }}
                />
        );
};

export default OtherOperationalIncome